<template>
  <section class="development-search-results">
    <!-- Search filters -->
    <DevelopmentSearchFilters />

    <div class="container-fluid">
      <div class="row __property-map-development-wrapper no-gutters" :style="developmentStore.viewType === 'Map' ? `height: ${propertyMapWrapperHeight}px;` : ''">
        <!-- List View -->
        <div v-if="['Map', 'List'].includes(displayViewType)" ref="developmentsWrapperRef" :class="developmentStore.viewType === 'Map' ? 'col-md-6' : 'col-md-12'" class="__property-development-block-wrapper">
          <!-- View change button -->
          <ElementsButtonsButtonGroup v-model="selectedView" :class="`--${displayViewType}`" :button-items="[{label: 'List', value: 'List', icon: listViewDarkIcon, selectedIcon: listViewIcon}, {label: 'Map', value: 'Map', icon: mapViewDarkIcon, selectedIcon: mapViewIcon}]" />

          <!-- Development List -->
          <DevelopmentSearchResultsList />

          <!-- Pagination -->
          <ElementsPagination :pagination-data="paginationData" @page-changed="setNewPage" />
        </div>

        <!-- Map View -->
        <div v-if="['Map', 'Mobile-Map'].includes(displayViewType)" :class="displayViewType === 'Map' ? 'col-md-6' : 'col-md-12'" class="__map-development-block-wrapper">
          <!-- View change button -->
          <ElementsButtonsButtonGroup v-model="selectedView" :class="`--${displayViewType} __mobile-map`" :button-items="[{label: 'List', value: 'List', icon: listViewDarkIcon, selectedIcon: listViewIcon}, {label: 'Map', value: 'Map', icon: mapViewDarkIcon, selectedIcon: mapViewIcon}]" />

          <!-- Development Map -->
          <client-only>
            <CommonMapLocation :height="propertyMapWrapperHeight" :markers="mapMarkers" filter-by-map-bounds show-position-fixed-map filter-by-map-bounds-store="development" />
          </client-only>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {ComputedRef, PropType, Ref} from 'vue';
import listViewIcon from '~/assets/images/icons/list-view.webp';
import mapViewIcon from '~/assets/images/icons/map-view.webp';
import listViewDarkIcon from '~/assets/images/icons/list-view-dark.webp';
import mapViewDarkIcon from '~/assets/images/icons/map-view-dark.webp';
import {DevelopmentWidget} from '~/@types/development';
import {DevelopmentViewType, useDevelopmentStore} from '~/store/development';
import {GoogleMapMarkerProp} from '~/@types/google-map';
import {Address, PaginationData} from '~/@types/common';
import {useRoute, useRouter} from '#app';
import DevelopmentSearchQueryHandler from '~/composables/developmentSearchQuery';
import {Listing} from '~/@types/listing';

// DATA
const developmentStore = useDevelopmentStore();
const propertyMapWrapperHeight: Ref<number> = ref(1440);
const screenWidth: Ref<number> = ref(1000);
const selectedView: Ref<DevelopmentViewType> = ref('Map');
const mapMarkers: Ref<GoogleMapMarkerProp[]> = ref([]);
const router = useRouter();
const route = useRoute();
const developmentsWrapperRef: Ref<HTMLDivElement | null> = ref(null);

// PROPS
const props = defineProps({
  builderInfo: {
    type: Object as PropType<any>,
    default: undefined,
  },
});

/**
 * Component MOUNTED!
 */
onMounted(() => {
  setPropertyMapWrapperHeight();
  if (window) {
    window.addEventListener('resize', setPropertyMapWrapperHeight);
    (window as any).openLink = (linkPath: string) => {
      if (route.path !== linkPath) {
        router.push(linkPath);
      }
    };

    if (mapMarkers.value.length < 1) {
      setMapMarkers();
    }
  }
});

/**
 * @_WATCH_ selectedView
 */
watch(() => selectedView.value, () => {
  developmentStore.setViewType(selectedView.value);
});

/**
 * @_WATCH_ developmentStore.developmentData
 */
watch(() => developmentStore.developmentData, (newValue, oldValue) => {
  if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
    setMapMarkers();
  }
}, {deep: true});

/**
 * Display view type
 */
const displayViewType: ComputedRef<DevelopmentViewType> = computed(() => {
  if (screenWidth.value > 0 && screenWidth.value < 768 && selectedView.value === 'Map') {
    return 'Mobile-Map';
  } else {
    return selectedView.value;
  }
});

/**
 * Set propertyMapWrapperHeight
 */
function setPropertyMapWrapperHeight () {
  if (window && window.innerHeight) {
    const windowInnerHeight = window.innerHeight;

    if (windowInnerHeight > 500 && windowInnerHeight < 1200) {
      propertyMapWrapperHeight.value = windowInnerHeight - 120;
    }

    if (screenWidth.value !== window.innerWidth) {
      screenWidth.value = window.innerWidth;
      if (screenWidth.value > 0 && screenWidth.value < 768) {
        selectedView.value = 'List';
      }
    }
  }
}

/**
 * Get map markers
 *
 * @param autoTryCount
 */
function setMapMarkers (autoTryCount = 0) {
  if (window && window.google && developmentStore.developmentData && Array.isArray(developmentStore.developmentData.hits)) {
    mapMarkers.value = developmentStore.developmentData.hits.filter((hit) => hit.document && hit.document.address && hit.document.address.latitude && hit.document.address.longitude).map((hit) => ({
      id: hit.document.slug,
      position:new google.maps.LatLng((hit.document.address as Address).latitude, (hit.document.address as Address).longitude),
      title: hit.document.name,
      infoWindowContent: getInfoWindowContent(hit.document),
      color: hit.document.type === 'Apartment' ? '#5b6e71' : '#16b406',
    }));
  } else if (autoTryCount < 10) {
    setTimeout(() => {
      setMapMarkers(autoTryCount + 1);
    }, 500);
  }
}

/**
 * Pagination data
 */
const paginationData: ComputedRef<PaginationData> = computed(() => {
  if (developmentStore.developmentData) {
    return {
      page: developmentStore.developmentData.page,
      totalRecords: developmentStore.developmentData.found,
      perPage: developmentStore.developmentData.request_params.per_page,
    };
  }
  return {
    page: 1,
    totalRecords: 0,
    perPage: 48,
  };
});

function setNewPage (pageNumber: number) {
  DevelopmentSearchQueryHandler.set({page: pageNumber});
  if (developmentsWrapperRef.value) {
    developmentsWrapperRef.value.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

/**
 * Get map info window content
 *
 * @param development
 */
function getInfoWindowContent (development: DevelopmentWidget) {
  return `<div class="map-development-info-window">
                              <div class="blocks-development">
                                <div class="__development-image-wrapper">
                                  <div class="__development-image" style="background-image: url('${development.heroImageUrl}');"></div>
                                </div>
                                <div class="__development-info-wrapper">
                                  <h4 class="__development-title"> ${development.name}</h4>
                                  <div class="__development-address-wrapper">
                                    <img class="__development-address-icon" src="/images/location.webp" alt="Location icon" data-not-lazy>
                                    <span class="__development-address-text"> ${development.address?.formattedAddress}</span>
                                  </div>
                                  <div class="__builder-logo-action-wrapper">
                                    <strong style="color: ${development.type === 'Apartment' ? '#5b6e71' : '#16b406'}; font-weight: 500; margin-top: 5px;">${development.type}</strong>
                                    <span class="__view-development-button" onclick="openLink('/developments/${development.slug}/view');">View&nbsp;&#62;</span>
                                  </div>
                                </div>
                              </div>
                            </div>`;
}
</script>

<style lang="scss">
.development-search-results {
  padding: 0 !important;

  .container-fluid {
    padding: 0;
    margin: 0;
  }

  .__property-map-development-wrapper {
    display: flex;
    position: relative;
    width: 100%;
    padding: 0;
    margin: 0;

    .__property-development-block-wrapper {
      max-height: fit-content;

      &.col-md-6 {
        max-height: 100%;
        overflow-y: auto;
      }
    }

    .blocks-development-map {
      height: 100%;
    }

    // Action row map list view switcher
    .el-buttons-button-group {
      position: absolute;
      width: fit-content;
      height: 40px;
      top: 30px;
      right: 30px;
      z-index: 9;

      &.__mobile-map {
        display: none;
      }

      &.--mobile-map.__mobile-map {
        display: flex;
        left: 12px;
        top: 8px;
      }

      .__full-view {
        .el-button-regular {
          margin-right: 0;
          height: 40px;
          border-radius: 8px;

          &:not(.--selected):not(:hover) {
            background: rgb(var(--background-light));
          }

          &:first-child {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          &:last-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }

        }
      }
    }
  }
}

// Map info window
.map-development-info-window {
  display: flex;
  width: 320px;
  height: 120px;

  .blocks-development {
    display: flex;
    width: 320px;
    height: 140px;

    .__development-image-wrapper {
      min-height: 120px;
      min-width: 120px;
      height: 120px;
      width: 120px;
      position: relative;
      border-radius: 0;

      .__development-image {
        width: 120px;
        height: 120px;
        background-size: cover;
        border-radius: 0;
      }
    }

    .__development-info-wrapper {
      padding: 12px;
      display: flex;
      flex-direction: column;

      .__development-title {
        color: rgb(var(--text-default));
        font-size: 16px;
        font-weight: 500;
      }

      .__development-address-wrapper {
        display: inline-flex;
        align-items: center;
        height: 40px;
        padding: 0;
        margin-top: 2px;

        .__development-address-text {
          font-size: 12px;
          color: rgb(var(--text-default));
        }

        img {
          width: 8px;
          height: auto;
          margin-right: 5px;
        }
      }

      .__builder-logo-action-wrapper {
        width: 100%;
        text-align: right;
        margin-top: 5px;
        height: fit-content;
        margin-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        img {
          width: 80px;
          height: auto;
        }

        .__view-development-button {
          background: #5C52C6FF;
          color: #fff;
          padding: 4px 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 400;
          box-shadow: 0 0 2px 0 #00000078;
          cursor: pointer;
          font-size: 12px;
          transition: box-shadow 0.2s ease-in-out;
          margin-left: 5px;

          &:hover {
            background: #341ee1;
            transition: all 0.2s ease-in-out;
          }
        }
      }
    }

    .__icon-info-bar {
      position: absolute;
      width: 100%;
      height: 32px;
      background: #fff;
      bottom: 0;
      box-shadow: 3px -5px 20px -14px #000000d9;
      padding: 8px 24px 6px 4px;
      display: inline-flex;
      justify-content: space-between;

      .__icon-info-item {
        display: flex;
        align-items: center;

        &:not(:last-child) {
          margin-right: 5px;
        }

        img {
          width: 15px;
          height: auto;
          margin-right: 8px;
        }

        span {
          font-weight: 500;
          color: rgba(var(--text-default), 0.6);
        }
      }
    }

    .__development-category {
      position: absolute;
      bottom: 26px;
      padding: 2px 5px;
      background: #fff;
      border-top-right-radius: 5px;
      box-shadow: 3px -5px 8px -4px #000000d9;
      color: rgba(var(--text-default), 0.5);
      font-weight: 400;
      z-index: 99;
    }
  }
}
</style>
